import { Fragment } from "react";
import type { Crumb } from "../../lib/api/types";

interface BreadcrumbProps {
  crumbs: Crumb[];
  onNavigate: (id: string) => void;
}

const MAX_VISIBLE = 4;

function Separator() {
  return (
    <span className="select-none px-1 text-text-muted" aria-hidden="true">
      /
    </span>
  );
}

function CrumbButton({ crumb, onNavigate }: { crumb: Crumb; onNavigate: (id: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => onNavigate(crumb.id)}
      className="max-w-[12rem] truncate rounded px-1 text-body text-text-muted hover:text-text hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      {crumb.name}
    </button>
  );
}

/**
 * Renders Home → current. Each non-last crumb is a button; the last is the
 * current location (non-clickable). Long trails collapse the middle with "…".
 */
export function Breadcrumb({ crumbs, onNavigate }: BreadcrumbProps) {
  if (crumbs.length === 0) {
    return null;
  }

  const lastIndex = crumbs.length - 1;

  // Collapse the middle when the trail is long: first, "…", last two.
  let rendered: Array<Crumb | "ellipsis">;
  if (crumbs.length > MAX_VISIBLE) {
    const first = crumbs[0]!;
    const tail = crumbs.slice(crumbs.length - 2);
    rendered = [first, "ellipsis", ...tail];
  } else {
    rendered = crumbs;
  }

  return (
    <nav aria-label="Breadcrumb" className="flex min-w-0 items-center overflow-hidden">
      {rendered.map((item, idx) => {
        if (item === "ellipsis") {
          return (
            <Fragment key="ellipsis">
              <span className="px-1 text-body text-text-muted" aria-hidden="true">
                …
              </span>
              <Separator />
            </Fragment>
          );
        }

        const isLast = item.id === crumbs[lastIndex]!.id && idx === rendered.length - 1;
        return (
          <Fragment key={item.id}>
            {isLast ? (
              <span
                aria-current="page"
                className="max-w-[16rem] truncate px-1 text-body font-medium text-text"
              >
                {item.name}
              </span>
            ) : (
              <>
                <CrumbButton crumb={item} onNavigate={onNavigate} />
                <Separator />
              </>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
